import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { GameSocket, loadSession } from './ws.js';
import type {
  ChatChannel,
  ClientMsg,
  HostView,
  Lang,
  PlayerView,
  ServerMsg,
} from '../../shared/src/types.js';
import { renderKnowledge } from '../../shared/src/knowledge.js';
import { errorMessage } from './errorCopy.js';
import { storageGet, storageSet } from './safeStorage.js';
import { countUnread, lastSeenMark, type SeenMark } from './chatUnread.js';
import { getFxPreference, setFxPreference, type FxPref } from './usePerfLite.js';
import { acquireWakeLock, shouldHoldWakeLock } from './wakeLock.js';
import { Home } from './screens/Home.js';
import { PlayerLobby } from './screens/PlayerLobby.js';
import { RoleReveal } from './screens/RoleReveal.js';
import { Night } from './screens/Night.js';
import { Day } from './screens/Day.js';
import { GameOver } from './screens/GameOver.js';
import { Codex } from './components/Codex.js';
import { Chat } from './components/Chat.js';
import { ROLE_BY_ID } from '../../shared/src/roles/index.js';

type ConnState = 'connecting' | 'open' | 'closed';

// Host-only and dev-only screens are code-split so a phone player never
// downloads them (HostLobby alone pulls in the `qrcode` dependency). The three
// host screens share one chunk via the screens/host.js barrel; the two dev
// entries (?dev=1 / ?dev=cards) each get their own.
const HostLobby = lazy(() => import('./screens/host.js').then((m) => ({ default: m.HostLobby })));
const HostNight = lazy(() => import('./screens/host.js').then((m) => ({ default: m.HostNight })));
const HostDay = lazy(() => import('./screens/host.js').then((m) => ({ default: m.HostDay })));
const DevConsole = lazy(() =>
  import('./screens/DevConsole.js').then((m) => ({ default: m.DevConsole })),
);
const CardGallery = lazy(() =>
  import('./screens/CardGallery.js').then((m) => ({ default: m.CardGallery })),
);

// `?dev=1`     → preview console (live game with bots)
// `?dev=cards` → card gallery (every role's RoleCard, no socket)
// Both short-circuit the normal flow.
const DEV_MODE = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('dev')
  : null;

// Validate the persisted language against the allowed set (audit P2) — a
// stale/corrupted value used to be cast straight to `Lang`.
function loadLang(): Lang {
  const v = storageGet('mafia.lang');
  return v === 'ru' || v === 'uk' ? v : 'ru';
}

export function App() {
  const [t, setT] = useState<Lang>(loadLang);

  useEffect(() => {
    storageSet('mafia.lang', t);
  }, [t]);

  // Keep the document language in sync so AT / the browser pick the right
  // pronunciation + hyphenation for the active locale.
  useEffect(() => {
    document.documentElement.lang = t;
  }, [t]);

  if (DEV_MODE === 'cards') {
    return (
      <div className="app-shell">
        <Suspense fallback={null}>
          <CardGallery t={t} />
        </Suspense>
      </div>
    );
  }
  if (DEV_MODE === '1') {
    return (
      <div className="app-shell">
        <Suspense fallback={null}>
          <DevConsole t={t} setT={setT} />
        </Suspense>
      </div>
    );
  }
  return <NormalApp t={t} setT={setT} />;
}

function NormalApp({ t, setT }: { t: Lang; setT: (t: Lang) => void }) {
  const sockRef = useRef<GameSocket | null>(null);
  const [conn, setConn] = useState<ConnState>('connecting');
  const [view, setView] = useState<PlayerView | null>(null);
  const [hostView, setHostView] = useState<HostView | null>(null);
  const [role, setRole] = useState<'idle' | 'host' | 'player'>('idle');
  // Keep the structured error code alongside the raw message so the banner can
  // localize it (errorCopy) and Home can read `.code`/`.message`. `seq` is a
  // monotonic id: the auto-clear timeout matches on it, not on the message
  // text (audit P2 — a repeated identical error let the FIRST timer clear the
  // SECOND banner early).
  const [error, setError] = useState<{ code?: string; message: string; seq: number } | null>(null);
  const errorSeqRef = useRef(0);
  // Global codex toggle — opens a roles browser from any screen.
  const [codexOpen, setCodexOpen] = useState(false);
  // Chat sheet. Players post; the host gets a read-only moderation view. Track
  // the IDENTITY of the last-seen message PER (room, channel) — not the array
  // length, which stops moving once the server's 80-message sliding window
  // caps (audit P2) — so the unread badge can tell which channel has activity
  // and reading one channel doesn't clear the others.
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeen, setChatSeen] = useState<
    Record<string, Partial<Record<ChatChannel, SeenMark>>>
  >({});
  // FX (decorative motion) preference, mirrored for the shell toggle.
  const [fxPref, setFxPrefState] = useState<FxPref>(() => getFxPreference());

  // Wire up socket once.
  useEffect(() => {
    const sock = new GameSocket();
    sockRef.current = sock;
    const offMsg = sock.onMessage((msg: ServerMsg) => {
      switch (msg.kind) {
        case 'session':
          setRole(msg.isHost ? 'host' : 'player');
          break;
        // Derive the role from the kind of view received, not just from the
        // session ack: when the server promotes this player to host mid-game
        // (host handoff), the next broadcast is a `host:view` with no new
        // `session` — without this, `role` stayed 'player' and the promoted
        // player was stuck on the ConnectingSplash until a full reconnect.
        case 'view':
          setView(msg.view);
          setHostView(null);
          setRole('player');
          break;
        case 'host:view':
          setHostView(msg.view);
          setView(null);
          setRole('host');
          break;
        case 'error': {
          const seq = ++errorSeqRef.current;
          setError({ code: msg.code, message: msg.message, seq });
          // Auto-clear non-fatal errors after a moment — but only the exact
          // banner instance this timer was armed for (by seq), so a repeat of
          // the same error restarts the full display window.
          setTimeout(
            () => setError((e) => (e && e.seq === seq ? null : e)),
            3500,
          );
          // NO_SESSION means our stale token is gone server-side (room expired
          // or server restart). Treat like a soft logout: drop local state so
          // the user lands back on Home instead of being stuck on a dead
          // host/player screen whose buttons just produce more errors.
          if (msg.code === 'NO_SESSION') {
            setView(null);
            setHostView(null);
            setRole('idle');
          }
          break;
        }
        case 'kicked':
          // 'displaced' = this session now lives in another tab; the token is
          // still valid THERE, so don't erase the shared localStorage key
          // (audit P1-12 — doing so soft-locked the surviving tab's reload).
          // ws.ts keeps the token too and stops auto-rejoining. Any other
          // reason means the server revoked the token — forget it.
          if (msg.reason !== 'displaced') sock.forgetSession();
          setView(null);
          setHostView(null);
          setRole('idle');
          break;
      }
    });
    const offConn = sock.onConnState(setConn);
    sock.connect();
    return () => {
      offMsg();
      offConn();
      // Crucial under React.StrictMode: the dev double-invoke of effects
      // would otherwise leak a live WebSocket per remount.
      sock.disconnect();
      sockRef.current = null;
    };
  }, []);

  // Once a session exists at app load, surface UI for the right role even
  // before the first view arrives — avoids a flash of Home for returning
  // players.
  useEffect(() => {
    const s = loadSession();
    if (s) setRole(s.isHost ? 'host' : 'player');
  }, []);

  // Stable send: reads sockRef.current at call-time, not render-time. Without
  // this, the very first render captures a noop because the effect that
  // assigns sockRef.current hasn't run yet — clicks on Home before the
  // socket is wired would silently no-op.
  const send = useCallback((msg: ClientMsg) => {
    sockRef.current?.send(msg);
  }, []);

  // Two STACKED, independent banner regions so a connection drop can't mask a
  // just-fired action error (they used to fight over one slot):
  //   - the connection strip (only when conn !== 'open'), role=status/polite,
  //   - a transient localized error region, role=alert/assertive.
  const ConnBanner =
    conn !== 'open' ? (
      <div className="disconnected-banner" role="status" aria-live="polite">
        {conn === 'closed'
          ? t === 'ru'
            ? 'СВЯЗЬ ПОТЕРЯНА · ПЕРЕПОДКЛЮЧАЕМСЯ'
            : 'ЗВ’ЯЗОК ВТРАЧЕНО · ПЕРЕПІДКЛЮЧАЄМОСЬ'
          : t === 'ru'
            ? 'ПОДКЛЮЧЕНИЕ…'
            : 'ПІДКЛЮЧЕННЯ…'}
      </div>
    ) : null;

  // Localize the error code (errorCopy); CSS owns the casing so no blanket
  // .toUpperCase() here.
  const ErrorBanner = error ? (
    <div className="error-banner" role="alert" aria-live="assertive">
      {errorMessage(error.code, t, error.message)}
    </div>
  ) : null;

  const hasBanner = conn !== 'open' || !!error;
  const Banner = (
    <>
      {ConnBanner}
      {ErrorBanner}
    </>
  );

  // Visually-hidden polite live region that announces phase changes (for AT)
  // independent of whichever subtree is mounted.
  const phase = view?.room.phase ?? hostView?.room.phase ?? null;

  // Keep the phone awake during active play (audit P3-8) so it doesn't sleep
  // mid-night-phase. Decision logic + the secure-context limitation (no-op on
  // plain LAN http) live in wakeLock.ts; this effect stays thin.
  const holdWake = shouldHoldWakeLock(role, phase);
  useEffect(() => {
    if (!holdWake) return;
    return acquireWakeLock();
  }, [holdWake]);

  const phaseAnnounce = phase ? phaseLabel(phase, t) : '';
  const PhaseLive = (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {phaseAnnounce}
    </div>
  );

  const myRoleId = view?.me.role?.id;

  // Codex floating action button + modal — present on every screen so the
  // user can always look up roles. Hidden during reveal stage so it doesn't
  // overlap the role card.
  const CodexFab = (
    <button
      className="codex-fab"
      onClick={() => setCodexOpen(true)}
      aria-label={t === 'ru' ? 'Справочник ролей' : 'Довідник ролей'}
      title={t === 'ru' ? 'Справочник ролей' : 'Довідник ролей'}
    >
      ?
    </button>
  );
  const CodexModal = codexOpen ? (
    <Codex t={t} onClose={() => setCodexOpen(false)} myRoleId={myRoleId} />
  ) : null;

  // Persistent shell controls (lang + motion) — available on EVERY screen and
  // role without threading setT/FX into each screen. Fixes "can't change
  // language after joining" and "no runtime FX recovery toggle". Hidden during
  // role-reveal like the FABs so they don't crowd the card.
  const cycleFx = () => {
    const next: FxPref = fxPref === 'auto' ? 'full' : fxPref === 'full' ? 'lite' : 'auto';
    setFxPreference(next);
    setFxPrefState(next);
  };
  const fxState =
    fxPref === 'lite'
      ? t === 'ru' ? 'ВЫКЛ' : 'ВИМК'
      : fxPref === 'full'
        ? t === 'ru' ? 'ВКЛ' : 'УВІМК'
        : 'АВТО';
  const fxLabel = `${t === 'ru' ? 'ЭФФЕКТЫ' : 'ЕФЕКТИ'}: ${fxState}`;
  const ShellControls = (
    <div className="shell-controls">
      <button
        className={`shell-lang-btn ${t === 'ru' ? 'on' : ''}`}
        onClick={() => setT('ru')}
        aria-pressed={t === 'ru'}
        aria-label="Русский"
      >
        RU
      </button>
      <button
        className={`shell-lang-btn ${t === 'uk' ? 'on' : ''}`}
        onClick={() => setT('uk')}
        aria-pressed={t === 'uk'}
        aria-label="Українська"
      >
        UA
      </button>
      <button
        className="shell-fx-btn mono"
        onClick={cycleFx}
        aria-label={fxLabel}
        title={fxLabel}
      >
        ✨ {fxState}
      </button>
    </div>
  );

  // Chat FAB + slide-up sheet. Players post; the host gets a read-only
  // moderation view of every channel. Unread is tracked per (room, channel):
  // a per-channel count of messages newer than the last one the user saw, so
  // reading one channel doesn't clear the others and the badge can tell which
  // channel has activity.
  const chatMap = view?.chat ?? hostView?.chat;
  const chatRoomCode = view?.room.code ?? hostView?.room.code ?? '';
  const seenForRoom = chatSeen[chatRoomCode] ?? {};
  let unread = 0;
  (['public', 'mafia', 'dead'] as ChatChannel[]).forEach((ch) => {
    unread += countUnread(chatMap?.[ch], seenForRoom[ch]);
  });

  // Mark only the actively-read channel seen — called from Chat as the active
  // tab / message count changes.
  const markChannelSeen = useCallback(
    (ch: ChatChannel) => {
      const mark = lastSeenMark((view?.chat ?? hostView?.chat)?.[ch]);
      const code = view?.room.code ?? hostView?.room.code ?? '';
      if (!code || !mark) return;
      setChatSeen((s) => {
        const room = s[code] ?? {};
        if (room[ch]?.id === mark.id) return s;
        return { ...s, [code]: { ...room, [ch]: mark } };
      });
    },
    [view, hostView],
  );

  const ChatFab =
    chatMap && !chatOpen ? (
      <button
        className="chat-fab"
        onClick={() => setChatOpen(true)}
        aria-label={t === 'ru' ? 'Чат' : 'Чат'}
        title={t === 'ru' ? 'Чат' : 'Чат'}
      >
        ✉
        {unread > 0 && <span className="chat-fab-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
    ) : null;
  const ChatSheet =
    view && chatOpen ? (
      <div className="chat-overlay" onClick={() => setChatOpen(false)}>
        <Chat
          t={t}
          view={view}
          send={send}
          onClose={() => setChatOpen(false)}
          onSeenChannel={markChannelSeen}
        />
      </div>
    ) : null;
  // Host moderation chat — read-only, every channel, driven by hostView.chat.
  const HostChatSheet =
    hostView && chatOpen ? (
      <div className="chat-overlay" onClick={() => setChatOpen(false)}>
        <Chat
          t={t}
          chat={hostView.chat}
          canPost={false}
          send={send}
          onClose={() => setChatOpen(false)}
          onSeenChannel={markChannelSeen}
        />
      </div>
    ) : null;

  // Reserve double banner height when a connection drop and an action error
  // coincide (both render, stacked).
  const twoBanners = conn !== 'open' && !!error;
  const shellClass = `app-shell${hasBanner ? ' has-banner' : ''}${twoBanners ? ' has-two-banners' : ''}`;

  // Idle = not in a room yet. Home owns its own RU/UA toggle, so the shell
  // controls are intentionally omitted here (avoids a duplicate switcher).
  if (role === 'idle') {
    return (
      <div className={shellClass}>
        {Banner}
        {PhaseLive}
        <Home t={t} setT={setT} send={send} lastError={error} />
        {CodexFab}
        {CodexModal}
      </div>
    );
  }

  // Host UI
  if (role === 'host') {
    if (!hostView) {
      return (
        <div className={shellClass}>
          {Banner}
          {PhaseLive}
          <ConnectingSplash t={t} kind="host" />
          {ShellControls}
          {CodexFab}
          {CodexModal}
        </div>
      );
    }
    const hostRevealing = hostView.room.phase === 'role-reveal';
    // HostLobby (lobby + role-reveal phases) carries its own RU/UA toggle, so
    // skip the shell controls there to avoid a duplicate switcher.
    const hostHasOwnLang = hostView.room.phase === 'lobby' || hostRevealing;
    return (
      <div className={shellClass}>
        {Banner}
        {PhaseLive}
        {/* The host screens are lazy (see top of file) — while their chunk
            streams in, show the same splash used before the first view. */}
        <Suspense fallback={<ConnectingSplash t={t} kind="host" />}>
          {hostView.room.phase === 'lobby' && <HostLobby t={t} setT={setT} view={hostView} send={send} />}
          {hostView.room.phase === 'role-reveal' && <HostLobby t={t} setT={setT} view={hostView} send={send} revealing />}
          {hostView.room.phase === 'night' && <HostNight t={t} view={hostView} send={send} />}
          {(hostView.room.phase === 'day-discussion' ||
            hostView.room.phase === 'day-vote' ||
            hostView.room.phase === 'day-result') && <HostDay t={t} view={hostView} send={send} />}
        </Suspense>
        {hostView.room.phase === 'game-over' && (
          <GameOver
            t={t}
            winner={hostView.room.winner}
            roleById={ROLE_BY_ID}
            summary={hostView.room.summary}
            isHost
            send={send}
          />
        )}
        {/* Hide the FABs during reveal so they don't crowd the card; the shell
            controls are additionally hidden whenever HostLobby shows its own
            language toggle. */}
        {!hostRevealing && (
          <>
            {ChatFab}
            {CodexFab}
          </>
        )}
        {!hostHasOwnLang && ShellControls}
        {HostChatSheet}
        {CodexModal}
      </div>
    );
  }

  // Player UI
  if (!view) {
    return (
      <div className={shellClass}>
        {Banner}
        {PhaseLive}
        <ConnectingSplash t={t} kind="player" />
        {ShellControls}
        {CodexFab}
        {CodexModal}
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {Banner}
      {PhaseLive}
      {view.flash && (
        <div className="flash" aria-live="assertive" aria-atomic="true">
          {renderKnowledge(view.flash, t, ROLE_BY_ID)}
        </div>
      )}
      {view.room.phase === 'lobby' && <PlayerLobby t={t} view={view} send={send} />}
      {view.room.phase === 'role-reveal' && <RoleReveal t={t} view={view} send={send} />}
      {view.room.phase === 'night' && <Night t={t} view={view} send={send} />}
      {(view.room.phase === 'day-discussion' ||
        view.room.phase === 'day-vote' ||
        view.room.phase === 'day-result') && <Day t={t} view={view} send={send} />}
      {view.room.phase === 'game-over' && (
        <GameOver
          t={t}
          winner={view.room.winner}
          roleById={ROLE_BY_ID}
          myRole={view.me.role ?? undefined}
          summary={view.room.summary}
          isHost={false}
          meId={view.me.id}
          send={send}
        />
      )}
      {/* Hide the codex/chat FABs + shell controls during role-reveal so they
          don't overlap the card or the "memorize and hide" gesture target. */}
      {view.room.phase !== 'role-reveal' && (
        <>
          {ChatFab}
          {ShellControls}
          {CodexFab}
        </>
      )}
      {ChatSheet}
      {CodexModal}
    </div>
  );
}

// Localized phase name for the off-screen aria-live announcer. Keep
// human-readable (not the noir uppercase) since it's spoken, not shown.
function phaseLabel(phase: string, t: Lang): string {
  const map: Record<string, Record<Lang, string>> = {
    lobby: { ru: 'Лобби', uk: 'Лобі' },
    'role-reveal': { ru: 'Раздача ролей', uk: 'Роздача ролей' },
    night: { ru: 'Ночь', uk: 'Ніч' },
    'day-discussion': { ru: 'День — обсуждение', uk: 'День — обговорення' },
    'day-vote': { ru: 'День — голосование', uk: 'День — голосування' },
    'day-result': { ru: 'День — итог', uk: 'День — підсумок' },
    'game-over': { ru: 'Игра окончена', uk: 'Гру завершено' },
  };
  return map[phase]?.[t] ?? phase;
}

// Brief splash shown after `session` arrives but before the first `view`.
// Without this, the user briefly sees Home again after tapping Join — which
// looks like the action did nothing.
function ConnectingSplash({ t, kind }: { t: Lang; kind: 'host' | 'player' }) {
  return (
    <div className="scr scr-home">
      <div className="bg-noir" />
      <div className="vig vignette-pulse" />
      <div className="home-logo">
        <h1 className="display home-title" style={{ fontSize: 70 }}>
          {kind === 'host'
            ? t === 'ru' ? 'СОЗДАЁМ…' : 'СТВОРЮЄМО…'
            : t === 'ru' ? 'ПОДКЛЮЧЕНО' : 'ПІДКЛЮЧЕНО'}
        </h1>
        <div className="home-sub sub dot-pulse">
          {t === 'ru' ? 'ЗАГРУЖАЕМ ИГРОВОЕ СОСТОЯНИЕ' : 'ЗАВАНТАЖУЄМО СТАН ГРИ'}
        </div>
      </div>
    </div>
  );
}
